import React, { useState, useEffect, useRef } from 'react';
import chefImage from '../assets/chef.jpg';
import centerImage from '../assets/5.jpg';
import expLogo from '../assets/exp.png'; // Import your new image
import { Helmet } from 'react-helmet-async';

const AboutUsSection = () => {
  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null);
  const [displayedYears, setDisplayedYears] = useState(0);
  const [textAnimationKey, setTextAnimationKey] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768); // Detect mobile

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!inView) {
              setInView(true);
              setTextAnimationKey((prevKey) => prevKey + 1);
            }
          } else {
            if (inView) {
              setInView(false);
              setDisplayedYears(0);
            }
          }
        });
      },
      {
        threshold: 0.05,
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [inView]);

  useEffect(() => {
    if (inView && textAnimationKey > 0) {
      let currentYear = 0;
      const finalYear = 3;
      const intervalDuration = 300;

      const interval = setInterval(() => {
        currentYear += 1;
        if (currentYear <= finalYear) {
          setDisplayedYears(currentYear);
        } else {
          clearInterval(interval);
        }
      }, intervalDuration);

      return () => clearInterval(interval);
    } else if (!inView) {
      setDisplayedYears(0);
    }
  }, [inView, textAnimationKey]);

  const containerStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    padding: '80px 5%',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '40px',
    backgroundColor: '#fff',
    height: 'auto',           // ✅ Fit height to content
    minHeight: 'auto',        // ✅ Remove fixed screen height
    maxHeight: 'none',        // ✅ Allow it to grow with content
    overflow: 'visible',      // ✅ Ensure content isn’t clipped
    boxSizing: 'border-box',
  };
  

  const imageWrapperStyle = {
    position: 'relative',
    // FURTHER REDUCED SIZE FOR MOBILE:
    width: isMobile ? '240px' : '307px', // Even smaller fixed width for mobile
    height: isMobile ? '350px' : '440px', // Even smaller fixed height for mobile
    maxWidth: '307px', // Keep max-width for larger screens
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
    backgroundColor: '#f2f2f2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '200px', // This creates the circle/oval shape
    animation: inView ? 'fadeInLeft 1.2s ease-out forwards' : 'none',
    opacity: inView ? 1 : 0,
    // Adjust padding to accommodate smaller size and ensure no cutting
    padding: isMobile ? '0 10px' : '0', // Reduced padding
    boxSizing: 'border-box', // Ensure padding is included in the width calculation
  };

  const centerImageEffectWrapperStyle = {
    position: 'absolute',
    width: '100%',
    height: '100%',
    borderRadius: '200px',
    overflow: 'hidden',
    zIndex: 1,
  };

  const centerImageStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '200px',
  };

  const badgeStyle = {
    position: 'absolute',
    top: '75px',
    right: '-10px', // Keep original offset
    backgroundColor: '#D6A54F',
    color: 'white',
    // FURTHER REDUCED SIZE FOR MOBILE:
    width: isMobile ? '70px' : '100px',
    height: isMobile ? '70px' : '100px',
    borderRadius: '12px',
    boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingLeft: '10px', // Adjusted padding for smaller badge
    zIndex: 10,
    animation: 'slideBadge 2s ease-in-out infinite alternate',
    fontFamily: 'Open Sans, sans-serif',
    fontWeight: 'bold',
  };

  const chefCircleStyle = {
    position: 'absolute',
    bottom: '28px',
    left: '0',
    transform: 'translateX(-50%)', // Keep original transform
    // FURTHER REDUCED SIZE FOR MOBILE:
    width: isMobile ? '90px' : '130px',
    height: isMobile ? '90px' : '130px',
    borderRadius: '50%',
    border: '4px solid white',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    zIndex: 10,
  };

  const chefImageStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  };

  const textContainerStyle = {
    flex: '1 1 400px',
    animation: inView ? 'fadeInRight 1.5s ease-out forwards' : 'none',
    opacity: inView ? 1 : 0,
    // Add padding for the text container on mobile as well
    padding: isMobile ? '0 15px' : '0', // Adjusted padding for mobile text
  };

  const subHeadingBaseStyle = {
    color: '#D4A762',
    fontSize: '18px',
    marginBottom: '1px',
    letterSpacing: '1px',
    transform: 'translateY(20px)',
    opacity: 0,
    fontWeight: '500',
  };

  const headingBaseStyle = {
    fontSize: 'clamp(30px, 5vw, 42px)',
    fontWeight: '500',
    lineHeight: '1.3',
    marginBottom: '20px',
    fontFamily: 'Playfair Display, serif',
    textAlign: 'left',
    transform: 'translateY(20px)',
    opacity: 0,
  };

  const paragraphBaseStyle = {
    fontSize: '16px',
    color: '#444',
    marginBottom: '25px',
    transform: 'translateY(20px)',
    opacity: 0,
  };

  const listItemBaseStyle = {
    marginBottom: '10px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    transform: 'translateY(20px)',
    opacity: 0,
  };

  const getAnimatedStyle = (baseStyle, delay, animateType = 'default') => {
    if (!inView) return baseStyle;

    if (animateType === 'list') {
      return {
        ...baseStyle,
        animation: `fadeSlideUp 0.6s ease ${delay}s forwards`,
      };
    }

    return {
      ...baseStyle,
      transform: 'translateY(0)',
      opacity: 1,
      transition: `transform 1s ease ${delay}s, opacity 1s ease ${delay}s`,
    };
  };

  const animatedSubHeading = getAnimatedStyle(subHeadingBaseStyle, 0.3);
  const animatedHeading = getAnimatedStyle(headingBaseStyle, 0.5);
  const animatedParagraph = getAnimatedStyle(paragraphBaseStyle, 0.7);

  const badgeLogoStyle = {
    // Also scale down the logo within the badge
    width: isMobile ? '20px' : '30px', // Further reduced logo size
    height: isMobile ? '20px' : '30px',
    objectFit: 'contain',
    marginBottom: '5px',
  };
  const badgeTextStyle = {
    fontSize: isMobile ? '10px' : '14px', // Further reduced font size in badge
    lineHeight: '1em',
    fontFamily: 'Open Sans',
    fontWeight: 700,
    color: 'white'
  };


  return (
    <>
    <Helmet>
  <title>About Us | The Porch Cafe – Rooftop Veg Restaurant in Kota</title>
  <meta name="description" content="Learn about The Porch Cafe in Kota – a rooftop vegetarian restaurant offering seasonal, locally sourced dishes, curated with care and excellence." />
  <meta name="robots" content="index, follow" />
  <link rel="canonical" href="https://admin.theporch.in/about" />
  
  {/* Open Graph for Social Sharing */}
  <meta property="og:title" content="About Us | The Porch Cafe – Rooftop Veg Restaurant in Kota" />
  <meta property="og:description" content="The Porch Cafe is a rooftop vegetarian restaurant in Kota, offering flavorful dishes and warm ambiance." />
  <meta property="og:image" content="https://admin.theporch.in/logo.png" />
  <meta property="og:url" content="https://admin.theporch.in/about" />
  <meta property="og:type" content="restaurant" />

  {/* Twitter Card */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="About Us | The Porch Cafe – Rooftop Veg Restaurant in Kota" />
  <meta name="twitter:description" content="Discover The Porch Cafe – Kota's rooftop dining experience with seasonal, vegetarian dishes crafted for food lovers." />
  <meta name="twitter:image" content="https://admin.theporch.in/logo.png" />

  {/* Structured Data: JSON-LD */}
  <script type="application/ld+json">
    {`
      {
        "@context": "https://schema.org",
        "@type": "Restaurant",
        "name": "The Porch Cafe & Restaurant",
        "image": "https://admin.theporch.in/logo.png",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Kota",
          "addressRegion": "Rajasthan",
          "postalCode": "324005",
          "streetAddress": "Hotel The Pride, Talwandi"
        },
        "url": "https://admin.theporch.in/about",
        "telephone": "+91 9024283004",
        "servesCuisine": ["North Indian", "Chinese", "Italian", "Fast Food", "Vegetarian"],
        "description": "The Porch Cafe is a rooftop vegetarian restaurant in Kota known for its locally sourced, seasonal dishes and warm ambiance.",
        "founder": {
          "@type": "Person",
          "name": "The Porch Team"
        },
        "logo": "https://admin.theporch.in/logo.png",
        "sameAs": [
          "https://www.instagram.com/_the.porch/",
          "https://www.facebook.com/people/The-Porch-Cafe-Restaurant/100083751721732/",
          "https://www.swiggy.com/city/kota/the-porch-cafe-and-restaurant-hotel-the-pride-talwandi-rest888157",
          "https://www.zomato.com/kota/the-porch-cafe-restaurant-talwandi"
        ]
      }
    `}
  </script>
</Helmet>

      <style>
        {`
          @keyframes fadeInLeft {
            0% { opacity: 0; transform: translateX(-50px); }
            100% { opacity: 1; transform: translateX(0); }
          }

          @keyframes fadeInRight {
            0% { opacity: 0; transform: translateX(50px); }
            100% { opacity: 1; transform: translateX(0); }
          }

          @keyframes slideBadge {
            from { transform: translateX(10px); }
            to { transform: translateX(40px); }
          }

          @keyframes fadeSlideUp {
            0% {
              opacity: 0;
              transform: translateY(20px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }

          li:hover {
            color: #D4A762;
            transform: translateX(10px);
            transition: all 0.3s ease;
          }

          .at-shiny-glass-effect:after {
            content: '';
            position: absolute;
            width: 200%;
            height: 0%;
            left: 50%;
            top: 50%;
            background-color: rgba(255,255,255,.3);
            transform: translate(-50%,-50%) rotate(-45deg);
            z-index: 1;
          }
          .at-shiny-glass-effect:hover:after {
            height: 250%;
            transition: all 600ms linear !important;
            background-color: transparent;
          }

          .badge-hover-fill {
            position: relative;
            overflow: hidden;
            z-index: 0;
            transition: color 0.4s ease, background-color 0.4s ease;
          }

          .badge-hover-fill::before {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 0%;
            background-color: #B98B4B;
            transition: height 0.4s ease-in-out;
            z-index: -1;
            border-radius: 12px;
          }

          .badge-hover-fill:hover::before {
            height: 100%;
          }

          .badge-hover-fill:hover .badge-text {
            color: white;
          }

          .badge-hover-fill:hover .badge-logo-img {
            filter: brightness(0) invert(1);
          }

          @media (min-width: 769px) {
            .about-us-section-desktop-height {
              min-height: 100vh;
            }
          }
        `}
      </style>

      <section id="about-us" ref={sectionRef} style={containerStyle} className="about-us-section-desktop-height">
        <div style={{ flex: '1 1 400px', display: 'flex', justifyContent: 'center' }}>
          <div style={imageWrapperStyle}>
            <div style={centerImageEffectWrapperStyle} className="at-shiny-glass-effect">
              <img src={centerImage} alt="Restaurant" style={centerImageStyle} loading="lazy" />
            </div>

            <div style={badgeStyle} className="badge-hover-fill">
              <img src={expLogo} alt="Experience Logo" style={badgeLogoStyle} className="badge-logo-img" />
              <div className="badge-text" style={badgeTextStyle}>
                {displayedYears}+ Years of
              </div>
              <div className="badge-text" style={badgeTextStyle}>
                Experience
              </div>
            </div>

            <div style={{ ...chefCircleStyle }} className="at-shiny-glass-effect">
              <img src={chefImage} alt="Chef" style={chefImageStyle} loading="lazy" />
            </div>
          </div>
        </div>

        <div style={textContainerStyle}>
          <h4 style={animatedSubHeading} key={`subheading-${textAnimationKey}`}>ABOUT US</h4>
          <h2 style={animatedHeading} key={`heading-${textAnimationKey}`}>
            Our Commitment To<br /> Authenticity And{' '}
            <span style={{ color: '#D4A762', fontFamily: 'Playfair Display, serif' }}>Excellence</span>
          </h2>

          <p style={animatedParagraph} key={`paragraph-${textAnimationKey}`}>
            Every dish we create is a celebration of connection, crafted with passion and inspired by diverse flavors.
            Join us in an inviting space where every bite sparks joy and every moment becomes a cherished memory.
          </p>

          <ul style={{ padding: 0, listStyle: 'none', color: '#333', fontSize: '15px' }}>
            {[
              'Seasonal & Locally Sourced Ingredients',
              'Vegetarian & Dietary-Friendly Options',
              'Exquisite Pairings & Unique Flavors',
              'Seasonal & Locally Sourced Ingredients',
            ].map((item, idx) => (
              <li
                key={`list-item-${textAnimationKey}-${idx}`}
                style={getAnimatedStyle(listItemBaseStyle, 0.9 + idx * 0.2, 'list')}
              >
                <span
                  style={{
                    height: '8px',
                    width: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#D4A762',
                    display: 'inline-block',
                    marginRight: '12px',
                  }}
                ></span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
};

export default AboutUsSection;